import React, { useEffect } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { type Group, SUGGESTED_GROUP } from '../../../constants.tsx';
import type { FieldGroupComponentProps } from '../../../types.tsx';
import messages from '../messages.tsx';
import {
	FieldCategoryTitle,
	FieldCategoryWrapper,
	NoStyle,
	PaginationButtonsContainer,
} from '../styled.tsx';
import { endSearchInteraction, getItemWidth } from '../utils.tsx';
import { WhatIsThis } from '../what-is-this/index.tsx';

type TriggerLoadNextPageProps = {
	groupId: Group;
	endCursor: string;
	pageSize: number;
	onLoadMore: (
		group: Group,
		after: string,
		pageSize: number,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
};

const TriggerLoadNextPage = ({
	groupId,
	endCursor,
	pageSize,
	onLoadMore,
}: TriggerLoadNextPageProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		const analyticsEvent: UIAnalyticsEvent = createAnalyticsEvent({
			action: 'load more',
			actionSubject: 'availableItemsGroup',
		});
		onLoadMore(groupId, endCursor, pageSize, analyticsEvent);
	}, [groupId, endCursor, pageSize, onLoadMore, createAnalyticsEvent]);
	return null;
};

export type Props = FieldGroupComponentProps & {
	messages: {
		suggestedGroupContent: MessageDescriptor;
	};
};

export const FieldGroup = ({
	title,
	groupId,
	items,
	pageSize,
	hasNextPage,
	droppableListIdPrefix,
	isDropDisabled,
	endCursor,
	onLoadMore,
	onRenderItem,
	getDropAnimationWidth,
	// TODO: It does not seem to be any variations of this component, refactor to use import instead of prop
	DroppableLayoutItemList,
	messages: injectedMessages,
}: Props) => {
	const { formatMessage } = useIntl();

	const onLoadMoreClick = (_: unknown, analyticsEvent: UIAnalyticsEvent) => {
		if (endCursor !== null) {
			onLoadMore(groupId, endCursor, pageSize, analyticsEvent);
		}
	};

	return (
		<>
			{title && (
				<FieldCategoryWrapper>
					<FieldCategoryTitle>{title}</FieldCategoryTitle>
					{groupId === SUGGESTED_GROUP && (
						<WhatIsThis
							content={formatMessage(injectedMessages.suggestedGroupContent)}
							buttonText={formatMessage(messages.moreInformationAboutTheSuggestedFieldsButtonText)}
							componentId="suggested-fields-section"
						/>
					)}
				</FieldCategoryWrapper>
			)}
			{hasNextPage && endCursor !== null && items.length < 2 ? (
				<TriggerLoadNextPage
					groupId={groupId}
					endCursor={endCursor}
					pageSize={pageSize}
					onLoadMore={onLoadMore}
				/>
			) : null}
			<DroppableLayoutItemList
				id={`${droppableListIdPrefix}${title}`}
				items={items}
				isDropDisabled={isDropDisabled}
				isDragDisabled={false}
				renderItem={onRenderItem}
				ListStyle={NoStyle}
				getDropAnimationWidth={getDropAnimationWidth}
				getItemWidth={getItemWidth}
				dropHighlight={false}
				onRender={endSearchInteraction}
				minimumItemCount={0}
			/>
			{hasNextPage && endCursor !== null ? (
				<PaginationButtonsContainer>
					<Button appearance="subtle-link" onClick={onLoadMoreClick}>
						{formatMessage(messages.loadMoreButtonText, {
							count: pageSize,
						})}
					</Button>
				</PaginationButtonsContainer>
			) : null}
		</>
	);
};
